import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = { code: "", description: "", active: true, gstBasedOnMrp: false };

const ProductCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/product-categories", { size: 200 });
      setCategories(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      code: category.code || "",
      description: category.description || "",
      active: category.active ?? true,
      gstBasedOnMrp: Boolean(category.gstBasedOnMrp),
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/v1/product-categories/${editingId}`, formData);
        toast.success("Category updated");
      } else {
        await api.post("/api/v1/product-categories", formData);
        toast.success("Category created");
      }
      setShowModal(false);
      loadCategories();
    } catch (error) {
      toast.error(error.message || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const filteredCategories = categories.filter((category) =>
    [category.code, category.description].some((value) => String(value || "").toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading categories...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Define the product category codes your business uses (e.g. "AG" = Agri, "CG" = Cigarettes) — these show up
        as a dropdown when creating/editing a product.
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search code / description"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="primary" onClick={openCreate}>
          + New Category
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
              <th title="Invoice lines of this product type are priced from the MRP (GST included) and the purchase basic rate">GST Based on MRP</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length === 0 ? (
              <tr>
                <td colSpan={5}>No categories found.</td>
              </tr>
            ) : (
              filteredCategories.map((category) => (
                <tr key={category.id}>
                  <td>{category.code}</td>
                  <td>{category.description}</td>
                  <td>{category.gstBasedOnMrp ? <span className="badge badge-blue">Yes</span> : "No"}</td>
                  <td>
                    <span className={`badge ${category.active ? "badge-green" : "badge-gray"}`}>
                      {category.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(category)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Category" : "Create Category"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Code</label>
                  <input name="code" value={formData.code} onChange={handleChange} required placeholder="e.g. CG" />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input name="description" value={formData.description} onChange={handleChange} required placeholder="e.g. Cigarettes" />
                </div>
                <div className="form-group">
                  <label>Active</label>
                  <select
                    name="active"
                    value={formData.active ? "true" : "false"}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === "true" }))}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" name="gstBasedOnMrp" checked={formData.gstBasedOnMrp} onChange={handleChange} />
                    GST based on MRP (e.g. ITC cigarettes)
                  </label>
                  <div className="page-subtitle">
                    Sales invoice lines of this product type take the MRP (GST included) and the purchase basic rate instead of a unit
                    price: taxable value = MRP / (1 + GST%), GST on it, distributor margin = (purchase basic rate + GST) x margin %, net
                    selling rate = purchase basic rate + margin + GST.
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProductCategories;
